import { Link, Navigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useCurrency } from "../lib/currency";
import { useTheme } from "../lib/theme";
import {
  FileText, Zap, Shield, ArrowRight, Check, ChevronDown,
  Target, Mail, Briefcase, BarChart3, Star, Sparkles,
  Sun, Moon, Menu, X, Clock, FileCheck, Upload, Chrome, Download
} from "lucide-react";
import { useState, useEffect, useRef } from "react";

const PLANS = [
  {
    id: "trial", name: "Free Trial", usd: 0, period: "",
    scans: "50 scans", badge: null,
    features: ["50 resume scans", "ATS scoring", "Email generation", "7-day access"],
    cta: "Start Free", ctaStyle: "secondary",
  },
  {
    id: "base", name: "Base", usd: 20, period: "/mo",
    scans: "200 scans/mo", badge: "Most Popular",
    features: ["200 resume scans/mo", "Cover letter generation", "Job application tracker", "Resume manager", "Scan add-ons available"],
    cta: "Get Started", ctaStyle: "primary",
  },
  {
    id: "unlimited", name: "Unlimited", usd: 35, period: "/mo",
    scans: "Unlimited scans", badge: null,
    features: ["Unlimited scans", "Everything in Base", "Priority AI processing", "Bulk optimization"],
    cta: "Go Unlimited", ctaStyle: "secondary",
  },
];

const FEATURES = [
  { icon: Target, title: "ATS Score Analysis", desc: "Instant keyword matching and scoring against any job description. Know exactly where your resume stands.", tag: "Core" },
  { icon: Sparkles, title: "AI Resume Rewriting", desc: "Our 3-stage AI pipeline analyzes, rewrites, and validates your content while preserving formatting.", tag: "AI" },
  { icon: Mail, title: "Recruiter Email Gen", desc: "Auto-generate professional recruiter outreach emails tailored to the job and your experience.", tag: "AI" },
  { icon: FileText, title: "Cover Letter Builder", desc: "Create professional, job-specific cover letters from your resume and job description in one click.", tag: "New" },
  { icon: Briefcase, title: "Job Application Tracker", desc: "Track every application with status updates, notes, and salary info all in one place.", tag: "Tool" },
  { icon: BarChart3, title: "Scan History & Analytics", desc: "Review past optimizations, compare scores, and track your improvement over time.", tag: "Data" },
];

const REVIEWS = [
  { name: "Sarah K.", role: "Software Engineer", text: "Went from 40% to 92% ATS score on my first scan. Got 3 interview calls within a week.", rating: 5 },
  { name: "Michael R.", role: "Product Manager", text: "The combined resume + cover letter generation saves me hours. Best tool in my job search toolkit.", rating: 5 },
  { name: "Priya S.", role: "Data Analyst", text: "Finally, a tool that doesn't mess up my formatting. The DOCX output looks exactly like my original.", rating: 5 },
  { name: "James W.", role: "Marketing Director", text: "The recruiter email generator is a game changer. Personalized outreach in seconds.", rating: 4 },
];

const FAQ = [
  { q: "How does the AI optimization work?", a: "Our 3-stage pipeline first analyzes your resume against the job description, then rewrites content to maximize keyword matches, and finally validates that everything reads naturally. Your original formatting is preserved." },
  { q: "Will my resume formatting be changed?", a: "No. We extract text, optimize it, then inject it back into your original document. Fonts, tables, colors, and layouts remain intact." },
  { q: "What file formats are supported?", a: "We support DOCX and PDF uploads. The optimized output is always a clean DOCX file." },
  { q: "How many scans do I get with the free trial?", a: "The free trial includes 50 scans over 7 days. No credit card required to start." },
  { q: "Can I generate a cover letter and email in the same request?", a: "Yes! When optimizing your resume, you can check the options to generate a cover letter and recruiter email simultaneously, saving time and tokens." },
  { q: "Is my data secure?", a: "Absolutely. Your resumes are processed and stored securely. We never share your data with third parties." },
];

function useInView(ref, threshold = 0.15) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setVisible(true); }, { threshold });
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref, threshold]);
  return visible;
}

function AnimatedSection({ children, className = "", delay = 0 }) {
  const ref = useRef(null);
  const visible = useInView(ref);
  return (
    <div ref={ref} className={`lp-animated ${visible ? "lp-visible" : ""} ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
}

export default function LandingPage() {
  const { user, loading } = useAuth();
  const { currency, toggleCurrency, formatPrice } = useCurrency();
  const { theme, toggleTheme } = useTheme();
  const [mobileMenu, setMobileMenu] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  if (loading) return null;
  if (user) return <Navigate to="/app" replace />;

  return (
    <div className="lp" data-testid="landing-page">
      {/* Header */}
      <header className="lp-header" data-testid="lp-header">
        <div className="lp-header-inner">
          <div className="lp-logo">
            <div className="lp-logo-icon"><FileCheck size={18} /></div>
            <span>ResumeAI</span>
          </div>
          <nav className="lp-nav-desktop">
            <a href="#features" className="lp-nav-link">Features</a>
            <a href="#pricing" className="lp-nav-link">Pricing</a>
            <a href="#reviews" className="lp-nav-link">Reviews</a>
            <a href="#faq" className="lp-nav-link">FAQ</a>
          </nav>
          <div className="lp-header-actions">
            <button className="lp-theme-toggle" onClick={toggleTheme} data-testid="theme-toggle-landing" aria-label="Toggle theme">
              {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            <Link to="/login" className="lp-nav-link" data-testid="nav-login">Sign In</Link>
            <Link to="/signup" className="lp-btn lp-btn-primary" data-testid="nav-signup">
              Start Free Trial
            </Link>
            <button className="lp-mobile-toggle" onClick={() => setMobileMenu(!mobileMenu)} aria-label="Menu">
              {mobileMenu ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
        {mobileMenu && (
          <div className="lp-mobile-menu" data-testid="mobile-menu">
            <a href="#features" className="lp-mobile-link" onClick={() => setMobileMenu(false)}>Features</a>
            <a href="#pricing" className="lp-mobile-link" onClick={() => setMobileMenu(false)}>Pricing</a>
            <a href="#reviews" className="lp-mobile-link" onClick={() => setMobileMenu(false)}>Reviews</a>
            <a href="#faq" className="lp-mobile-link" onClick={() => setMobileMenu(false)}>FAQ</a>
            <Link to="/login" className="lp-mobile-link" onClick={() => setMobileMenu(false)}>Sign In</Link>
            <Link to="/signup" className="lp-btn lp-btn-primary" style={{ width: "100%" }} onClick={() => setMobileMenu(false)}>Start Free Trial</Link>
          </div>
        )}
      </header>

      {/* Hero */}
      <section className="lp-hero" data-testid="lp-hero">
        <div className="lp-hero-glow" />
        <div className="lp-hero-content">
          <div className="lp-hero-badge">
            <Sparkles size={14} />
            <span>AI-Powered Resume Optimization</span>
          </div>
          <h1 className="lp-hero-h1">
            Land more interviews<br />
            <span className="lp-gradient-text">with smarter resumes</span>
          </h1>
          <p className="lp-hero-sub">
            Upload your resume, paste a job description, and get an ATS-optimized version
            in seconds. Our AI rewrites, scores, and generates cover letters — all in one flow.
          </p>
          <div className="lp-hero-actions">
            <Link to="/signup" className="lp-btn lp-btn-primary lp-btn-lg" data-testid="hero-cta-primary">
              <Upload size={18} /> Start Optimizing Free
            </Link>
            <a href="#pricing" className="lp-btn lp-btn-outline lp-btn-lg" data-testid="hero-cta-pricing">
              View Pricing
            </a>
          </div>
          <div className="lp-hero-proof">
            <div className="lp-hero-stars">
              {[1,2,3,4,5].map(i => <Star key={i} size={14} fill="currentColor" />)}
            </div>
            <span>Trusted by 2,000+ job seekers</span>
          </div>
        </div>
        <div className="lp-hero-visual">
          <div className="lp-hero-mockup" data-testid="hero-mockup">
            <div className="lp-mockup-header">
              <div className="lp-mockup-dots"><span /><span /><span /></div>
              <span className="lp-mockup-title">Resume Optimizer</span>
            </div>
            <div className="lp-mockup-body">
              <div className="lp-mockup-score">
                <svg width="80" height="80" viewBox="0 0 80 80">
                  <circle cx="40" cy="40" r="34" fill="none" stroke="currentColor" strokeWidth="4" opacity="0.1" />
                  <circle cx="40" cy="40" r="34" fill="none" stroke="#10B981" strokeWidth="4"
                    strokeDasharray={`${2 * Math.PI * 34 * 0.92} ${2 * Math.PI * 34 * 0.08}`}
                    strokeLinecap="round" transform="rotate(-90 40 40)" />
                </svg>
                <div className="lp-mockup-score-text">
                  <span className="lp-mockup-score-num">92</span>
                  <span className="lp-mockup-score-label">ATS Score</span>
                </div>
              </div>
              <div className="lp-mockup-pills">
                <span className="lp-mockup-pill green">Keywords: 95%</span>
                <span className="lp-mockup-pill blue">Skills: 88%</span>
                <span className="lp-mockup-pill yellow">Experience: 90%</span>
              </div>
              <div className="lp-mockup-bar"><div className="lp-mockup-bar-fill" /></div>
              <div className="lp-mockup-bar short"><div className="lp-mockup-bar-fill" /></div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="lp-section" id="how-it-works">
        <AnimatedSection>
          <div className="lp-section-header">
            <span className="lp-section-tag">How It Works</span>
            <h2 className="lp-section-h2">Three steps to your best resume</h2>
          </div>
        </AnimatedSection>
        <div className="lp-steps">
          {[
            { num: "01", icon: Upload, title: "Upload Resume", desc: "Drop your DOCX or PDF. We preserve every font, table, and layout." },
            { num: "02", icon: Sparkles, title: "AI Optimizes", desc: "Our 3-stage pipeline analyzes, rewrites, and validates against the job description." },
            { num: "03", icon: FileText, title: "Download & Apply", desc: "Get your optimized resume, cover letter, and recruiter email — ready to send." },
          ].map((step, i) => (
            <AnimatedSection key={i} className="lp-step" delay={i * 150}>
              <div className="lp-step-num">{step.num}</div>
              <div className="lp-step-icon"><step.icon size={24} /></div>
              <h3>{step.title}</h3>
              <p>{step.desc}</p>
            </AnimatedSection>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="lp-section lp-section-alt" id="features">
        <AnimatedSection>
          <div className="lp-section-header">
            <span className="lp-section-tag">Features</span>
            <h2 className="lp-section-h2">Everything you need to land the job</h2>
            <p className="lp-section-sub">A complete toolkit for modern job seekers.</p>
          </div>
        </AnimatedSection>
        <div className="lp-features-grid">
          {FEATURES.map((f, i) => (
            <AnimatedSection key={i} className="lp-feature-card" delay={i * 100}>
              <div className="lp-feature-card-top">
                <div className="lp-feature-icon"><f.icon size={20} /></div>
                <span className="lp-feature-tag">{f.tag}</span>
              </div>
              <h3>{f.title}</h3>
              <p>{f.desc}</p>
            </AnimatedSection>
          ))}
        </div>
      </section>

      {/* Reviews */}
      <section className="lp-section" id="reviews">
        <AnimatedSection>
          <div className="lp-section-header">
            <span className="lp-section-tag">Reviews</span>
            <h2 className="lp-section-h2">Loved by job seekers worldwide</h2>
          </div>
        </AnimatedSection>
        <div className="lp-reviews-grid">
          {REVIEWS.map((r, i) => (
            <AnimatedSection key={i} className="lp-review-card" delay={i * 100}>
              <div className="lp-review-stars">
                {Array.from({ length: r.rating }).map((_, j) => <Star key={j} size={14} fill="currentColor" />)}
              </div>
              <p className="lp-review-text">"{r.text}"</p>
              <div className="lp-review-author">
                <div className="lp-review-avatar">{r.name[0]}</div>
                <div>
                  <div className="lp-review-name">{r.name}</div>
                  <div className="lp-review-role">{r.role}</div>
                </div>
              </div>
            </AnimatedSection>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section className="lp-section lp-section-alt" id="pricing">
        <AnimatedSection>
          <div className="lp-section-header">
            <span className="lp-section-tag">Pricing</span>
            <h2 className="lp-section-h2">Simple, transparent pricing</h2>
            <p className="lp-section-sub">Start free. Upgrade when you're ready.</p>
            <button className="lp-currency-toggle" onClick={toggleCurrency} data-testid="currency-toggle">
              <span className={currency === "USD" ? "active" : ""}>USD</span>
              <span className={currency === "INR" ? "active" : ""}>INR</span>
            </button>
          </div>
        </AnimatedSection>
        <div className="lp-pricing-grid">
          {PLANS.map((plan, i) => (
            <AnimatedSection key={plan.id} className={`lp-price-card ${plan.badge ? "featured" : ""}`} delay={i * 120}>
              {plan.badge && <div className="lp-price-badge">{plan.badge}</div>}
              <div className="lp-price-name">{plan.name}</div>
              <div className="lp-price-amount">
                {plan.usd === 0 ? (
                  <span className="lp-price-num">Free</span>
                ) : (
                  <>
                    <span className="lp-price-num">{formatPrice(plan.usd)}</span>
                    <span className="lp-price-period">{plan.period}</span>
                  </>
                )}
              </div>
              <div className="lp-price-scans">{plan.scans}</div>
              <ul className="lp-price-features">
                {plan.features.map((f, j) => (
                  <li key={j}><Check size={14} /> {f}</li>
                ))}
              </ul>
              <Link
                to="/signup"
                className={`lp-btn ${plan.ctaStyle === "primary" ? "lp-btn-primary" : "lp-btn-outline"} lp-btn-full`}
                data-testid={`pricing-cta-${plan.id}`}
              >
                {plan.cta} <ArrowRight size={16} />
              </Link>
            </AnimatedSection>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="lp-section" id="faq">
        <AnimatedSection>
          <div className="lp-section-header">
            <span className="lp-section-tag">FAQ</span>
            <h2 className="lp-section-h2">Frequently asked questions</h2>
          </div>
        </AnimatedSection>
        <div className="lp-faq-list" data-testid="faq-list">
          {FAQ.map((item, i) => (
            <AnimatedSection key={i} delay={i * 80}>
              <button
                className={`lp-faq-item ${openFaq === i ? "open" : ""}`}
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                data-testid={`faq-item-${i}`}
              >
                <div className="lp-faq-q">
                  <span>{item.q}</span>
                  <ChevronDown size={18} className="lp-faq-chevron" />
                </div>
                <div className="lp-faq-a">{item.a}</div>
              </button>
            </AnimatedSection>
          ))}
        </div>
      </section>

      {/* Chrome Extension Section */}
      <section className="lp-section" data-testid="chrome-extension-section">
        <AnimatedSection>
          <div className="lp-section-header">
            <span className="lp-section-badge">New</span>
            <h2 className="lp-section-title">Chrome Extension</h2>
            <p className="lp-section-desc">Optimize your resume directly from LinkedIn, Indeed, Dice, and any job posting page.</p>
          </div>
        </AnimatedSection>
        <AnimatedSection delay={0.1}>
          <div className="lp-ext-card" data-testid="chrome-extension-card">
            <div className="lp-ext-left">
              <div className="lp-ext-features">
                <div className="lp-ext-feature">
                  <Target size={16} className="lp-ext-icon" />
                  <div><strong>1-Click ATS Scan</strong><p>Instantly check your resume score against any job posting</p></div>
                </div>
                <div className="lp-ext-feature">
                  <Zap size={16} className="lp-ext-icon" />
                  <div><strong>Quick Optimize</strong><p>Optimize your resume without leaving the job page</p></div>
                </div>
                <div className="lp-ext-feature">
                  <FileText size={16} className="lp-ext-icon" />
                  <div><strong>Cover Letter & Email</strong><p>Generate tailored cover letters and recruiter emails</p></div>
                </div>
                <div className="lp-ext-feature">
                  <Briefcase size={16} className="lp-ext-icon" />
                  <div><strong>Auto Job Tracking</strong><p>Track applications directly from job pages</p></div>
                </div>
              </div>
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/extension/download`} className="lp-btn lp-btn-primary" data-testid="download-extension-landing" download>
                <Download size={16} /> Download Chrome Extension
              </a>
            </div>
            <div className="lp-ext-right">
              <div className="lp-ext-browser">
                <div className="lp-ext-browser-bar">
                  <span className="lp-ext-dot" /><span className="lp-ext-dot" /><span className="lp-ext-dot" />
                  <span className="lp-ext-url">linkedin.com/jobs/...</span>
                  <div className="lp-ext-icon-badge"><Chrome size={12} /></div>
                </div>
                <div className="lp-ext-browser-content">
                  <div className="lp-ext-popup-preview">
                    <div className="lp-ext-pp-header">ResumeAI</div>
                    <div className="lp-ext-pp-score">87%</div>
                    <div className="lp-ext-pp-label">ATS Score</div>
                    <div className="lp-ext-pp-btns">
                      <span>Scan</span><span>Optimize</span><span>Letter</span><span>Email</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </AnimatedSection>
      </section>

      {/* CTA */}
      <section className="lp-cta-section">
        <AnimatedSection>
          <div className="lp-cta-inner">
            <h2>Ready to optimize your resume?</h2>
            <p>Join thousands of job seekers landing more interviews with AI-optimized resumes.</p>
            <div className="lp-hero-actions">
              <Link to="/signup" className="lp-btn lp-btn-primary lp-btn-lg" data-testid="cta-bottom">
                <Sparkles size={18} /> Get Started Free
              </Link>
            </div>
          </div>
        </AnimatedSection>
      </section>

      {/* Footer */}
      <footer className="lp-footer" data-testid="lp-footer">
        <div className="lp-footer-inner">
          <div className="lp-footer-brand">
            <div className="lp-logo">
              <div className="lp-logo-icon"><FileCheck size={16} /></div>
              <span>ResumeAI</span>
            </div>
            <p>AI-powered resume optimization for modern job seekers.</p>
          </div>
          <div className="lp-footer-links">
            <div className="lp-footer-col">
              <h4>Product</h4>
              <a href="#features">Features</a>
              <a href="#pricing">Pricing</a>
              <a href="#faq">FAQ</a>
            </div>
            <div className="lp-footer-col">
              <h4>Company</h4>
              <a href="#reviews">Reviews</a>
              <Link to="/login">Sign In</Link>
              <Link to="/signup">Start Free Trial</Link>
            </div>
          </div>
        </div>
        <div className="lp-footer-bottom">
          <span>Built with AI. Designed for humans.</span>
        </div>
      </footer>
    </div>
  );
}
